import { defineMessage } from '@formatjs/intl';
import { SocialExportTikTokCreatorInfoDto } from '@mi-front/api-v2';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { tiktokDisclosureMessages as disclosureMessages } from './disclosure/tiktok-disclosure-messages';

export const tikTokPrivacyOptionMessages = {
  PUBLIC_TO_EVERYONE: defineMessage({
    id: 'socialExport.tiktok.privacy.public',
    defaultMessage: 'Público',
  }),
  MUTUAL_FOLLOW_FRIENDS: defineMessage({
    id: 'socialExport.tiktok.privacy.friends',
    defaultMessage: 'Amigos',
  }),
  SELF_ONLY: defineMessage({
    id: 'socialExport.tiktok.privacy.self',
    defaultMessage: 'Solo yo',
  }),
  FOLLOWER_OF_CREATOR: defineMessage({
    id: 'socialExport.tiktok.privacy.followers',
    defaultMessage: 'Seguidores',
  }),
};

const FALLBACK_PRIVACY_OPTIONS = [
  'PUBLIC_TO_EVERYONE',
  'MUTUAL_FOLLOW_FRIENDS',
  'FOLLOWER_OF_CREATOR',
  'SELF_ONLY',
] as const;

interface TiktokPrivacyFieldProps {
  /** Creator info from API; when provided, options come from privacy_level_options (TikTok guidelines). */
  creatorInfo?: SocialExportTikTokCreatorInfoDto | null;
}

/**
 * Renders the privacy level select for TikTok publish/settings.
 * Options are driven by creator_info when provided, otherwise uses fallback values.
 * When branded content disclosure is on, "only me" (SELF_ONLY) is disabled and shows a tooltip (TikTok guideline).
 */
export function TiktokPrivacyField({ creatorInfo }: TiktokPrivacyFieldProps) {
  const { formatMessage, $t } = useIntl();
  const { control } = useFormContext();
  const discloseVideoContent = useWatch({ name: 'discloseVideoContent' });
  const brandContent = useWatch({ name: 'brandContent' });
  const isBrandedContentOn = Boolean(discloseVideoContent && brandContent);
  const brandedContentTooltip = formatMessage(disclosureMessages.brandedContentVisibilityTooltip);

  const privacyOptions = useMemo(() => {
    const keys: readonly string[] = creatorInfo?.privacy_level_options?.length
      ? creatorInfo.privacy_level_options
      : [...FALLBACK_PRIVACY_OPTIONS];
    return keys.map((key: string) => {
      const message =
        key in tikTokPrivacyOptionMessages
          ? tikTokPrivacyOptionMessages[key as keyof typeof tikTokPrivacyOptionMessages]
          : { id: `socialExport.tiktok.privacy.${key}`, defaultMessage: key };
      return { id: key, label: formatMessage(message) };
    });
  }, [creatorInfo?.privacy_level_options, formatMessage]);

  return (
    <Controller
      name="privacyLevel"
      control={control}
      rules={{ required: true }}
      render={({ field, fieldState }) => (
        <FormControl fullWidth required error={Boolean(fieldState.error)}>
          <InputLabel id="tiktok-privacy-label">
            {$t({
              id: 'socialExport.tiktok.settings.privacyLabel',
              defaultMessage: 'Privacidad',
            })}
          </InputLabel>
          <Select
            {...field}
            labelId="tiktok-privacy-label"
            label={$t({
              id: 'socialExport.tiktok.settings.privacyLabel',
              defaultMessage: 'Privacidad',
            })}
            value={field.value ?? ''}
            onChange={(e: SelectChangeEvent<string>) => field.onChange(e.target.value)}
          >
            {privacyOptions.map((opt) => {
              const isOnlyMe = opt.id === 'SELF_ONLY';
              const disableOnlyMe = isOnlyMe && isBrandedContentOn;
              const menuItem = (
                <MenuItem key={opt.id} value={opt.id} disabled={disableOnlyMe}>
                  {opt.label}
                </MenuItem>
              );
              if (disableOnlyMe) {
                return (
                  <Tooltip key={opt.id} title={brandedContentTooltip} placement="left">
                    <span style={{ display: 'inline-flex', width: '100%' }}>{menuItem}</span>
                  </Tooltip>
                );
              }
              return menuItem;
            })}
          </Select>
        </FormControl>
      )}
    />
  );
}
